import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';
import { clearPortalCsrfToken } from '@/services/portalClient';
import { clearPortalQueries, subscribePortalSessionChanges } from '@/services/portalSessionSync';
import { portalLoginPath } from '@/services/portalTenantContext';

/** Shared cookies can change in another tab; each tab must discard its old identity. */
export function PortalSessionBoundary({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const { pathname, search } = useLocation();
  const [peerChanged, setPeerChanged] = useState(false);
  // Launch Center uses the independent staff session even under /portal.
  const isPartnerPortalPath =
    (pathname === '/portal' || pathname.startsWith('/portal/')) &&
    pathname !== '/portal/launch' &&
    !pathname.startsWith('/portal/launch/');

  useEffect(
    () =>
      subscribePortalSessionChanges(
        () => {
          clearPortalCsrfToken();
          void clearPortalQueries(client).catch(() => undefined);
          setPeerChanged(true);
        },
        () => setPeerChanged(false),
      ),
    [client],
  );

  if (
    peerChanged &&
    isPartnerPortalPath &&
    pathname !== '/portal/login'
  ) {
    const login = portalLoginPath(search);
    return <Navigate to={`${login}${login.includes('?') ? '&' : '?'}sessionChanged=1`} replace />;
  }
  return children;
}
